import { useMutation } from 'convex/react';
import { motion } from 'framer-motion';
import { FileTextIcon, MicIcon, ShieldCheckIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '~/convex/_generated/api';
import { Button } from '../ui/button';
import { StepIndicator } from './step-indicator';

interface InfoStepProps {
  onBack: () => void;
  currentStep: number;
  totalSteps: number;
}

export function InfoStep({ onBack, currentStep, totalSteps }: InfoStepProps) {
  const { t } = useTranslation();
  const updateSettings = useMutation(api.userSettings.updateSettings);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await updateSettings({ hasCompletedOnboarding: true });
    } catch (error) {
      console.error('Failed to complete onboarding', error);
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: MicIcon,
      title: t('onboarding:feature1Title'),
      description: t('onboarding:feature1Desc'),
    },
    {
      icon: FileTextIcon,
      title: t('onboarding:feature2Title'),
      description: t('onboarding:feature2Desc'),
    },
    {
      icon: ShieldCheckIcon,
      title: t('onboarding:feature3Title'),
      description: t('onboarding:feature3Desc'),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex h-full w-full max-w-xl flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">
          {t('onboarding:welcomeTitle')}
        </h1>
        <p className="text-muted-foreground">
          {t('onboarding:welcomeDescription')}
        </p>
      </div>

      <div className="flex grow flex-col gap-6 overflow-y-auto">
        {features.map((feature, index) => (
          <div key={index} className="flex items-center gap-4">
            <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
              <feature.icon className="h-6 w-6" />
            </div>
            <div className="flex flex-col gap-1">
              <h3 className="leading-none font-medium">{feature.title}</h3>
              <p className="text-muted-foreground text-sm">
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onBack}>
            {t('login:back')}
          </Button>
          <Button
            className="flex-1"
            onClick={handleAccept}
            loading={isSubmitting}
            disabled={isSubmitting}>
            {t('login:continue')}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
